import React, { useState, useEffect, useRef, FC, Dispatch, SetStateAction } from 'react'
import { useSelector } from 'react-redux'
import { getIndice } from 'selectors/algolia'
import { capitalizeString } from 'lib/helpers'
import { getArtworkId } from 'apps/saatchi/artdetail2/selectors/artwork'
import { getSelectedProductSku } from 'apps/saatchi/artdetail2/selectors/product'
import { getArtistInfo } from 'apps/saatchi/artdetail2/selectors/artist'
import { getUserID } from 'selectors/user/session'
import { getAlgoliaUserToken } from 'selectors/user/cookie'
import CarouselV2 from 'components/SA/Carousel/v2'
import { getArtistArtworks, clickArtworkCarousel, ALGOLIA } from '../helpers'
import Polaroid from '../ProductCarouselPolaroid'
import { getArtistSeeMore } from 'apps/saatchi/artdetail2/selectors/carousel'
import SeeMoreTile from '../SeeMoreTile'
import styles from '../ProductCarouselPolaroid.module.scss'
import useCarouselImageLazyLoading from 'components/SA/Carousel/v2/useCarouselImageLazyLoading'
const MIN_ARTWORKS = 5
const LIST_ID = 'more-from-artist'
const DATA_API = ALGOLIA

type ArtistArtworksCarouselProps = {
  setShouldLoadComponent: Dispatch<SetStateAction<boolean>>
}

const ArtistArtworksCarousel: FC<ArtistArtworksCarouselProps> = (props) => {
  const { setShouldLoadComponent } = props
  /* Replacing connector */
  const algoliaSearchIndex = useSelector(getIndice)
  const userId = useSelector(getUserID)
  const artworkId = useSelector(getArtworkId)
  const algoliaUserToken = useSelector(getAlgoliaUserToken)
  const sku = useSelector(getSelectedProductSku)
  const artistInfo = useSelector(getArtistInfo)
  const seeMore = useSelector(getArtistSeeMore)
  /* ============================================================== */
  const { link, title } = seeMore
  const [artworks, handleArtworks] = useState([])
  const polaroidRef = useRef<HTMLDivElement>(null)
  const [polaroidWidth, setPolaroidWidth] = useState<number>(
    polaroidRef?.current?.offsetWidth || 270
  )
  const { handleImageLazyLoading, lastImageIndexLoaded } = useCarouselImageLazyLoading({
    offset: 5,
  })
  const { firstName, lastName, userId: artistId } = artistInfo
  const capitalizedFullName = capitalizeString(`${firstName || ''} ${lastName || ''}`)
  const LIST_NAME = `More From ${capitalizedFullName}`
  useEffect(() => {
    getArtistArtworks({
      callBack: handleArtworks,
      firstName,
      lastName,
      sku,
      userId: artistId,
    })
  }, [artworkId])

  useEffect(() => {
    if (!!polaroidRef?.current?.offsetWidth) {
      setPolaroidWidth(polaroidRef?.current?.offsetWidth)
    }
  }, [polaroidRef?.current?.offsetWidth])

  const handleClick = (ev: React.SyntheticEvent<Record<string, any>>) => {
    setShouldLoadComponent(false)
    clickArtworkCarousel(ev, algoliaSearchIndex, algoliaUserToken, userId)
  }

  if (!artworks || artworks.length < MIN_ARTWORKS) {
    return null
  }

  return (
    <div data-section={LIST_ID}>
      <CarouselV2
        title={LIST_NAME}
        polaroidWidth={polaroidWidth}
        customStyle={styles.carouselPolaroidImage}
        onScrollCallback={handleImageLazyLoading}
      >
        {artworks.map((artwork, index) => (
          <Polaroid
            key={index}
            polaroidRef={polaroidRef}
            artwork={artwork}
            onImageClick={handleClick}
            onTitleClick={handleClick}
            index={index}
            listId={LIST_ID}
            listName={LIST_NAME}
            dataApi={DATA_API}
            showArtistName={false}
            isLazyLoading={index > lastImageIndexLoaded}
          />
        ))}
        <SeeMoreTile link={link} title={title} />
      </CarouselV2>
    </div>
  )
}

export default ArtistArtworksCarousel
